import { styled } from '@mui/system';
import { useForm } from 'react-hook-form';
import { useContext, useEffect, useState } from 'react';

import * as Regex from 'utils/validations';
import { DataContext } from 'providers/data';

import Box from '@mui/material/Box';
import Input from 'components/views/form/Input';
import Typography from '@mui/material/Typography';
import Select from 'components/views/form/Select';
import ContentFadeAnimation from 'components/views/FadeAnimation';

type ParamsVehicle = {
    brand?: { text: string; value: string };
    model?: { text: string; value: string };
    version?: { text: string; value: string };
    year?: { text: string; value: string };
    color?: { text: string; value: string };
    chassisOrBoard?: string;
    yearManufacture?: { text: string; value: string };
    chassis?: string;

    yearId?: number;
    brandId?: number;
    modelId?: number;
    versionId?: number;
}

type ParamsVehicleDatas = {
    handleReturnFormData: (type: ParamsReturnFormVehicle) => void;
}

type ParamsReturnFormVehicle = {
    year: string;
    chassis: string;
    version: number;
    yearManufacture: string;
    color: string;
}

type ParamsArray = {
    text: string;
    value: string;
}

const TitleForm = styled(Typography)
    (({ theme }) => `
        font-weight: 700;
        text-align: center;
        font-size: 1.25rem;
        
        @media(min-width: ${theme.breakpoints.values.md}px) {
            font-weight: 400;
            font-size: 1.875rem;
            padding-bottom: .56rem;
        }
`)

const BoxFlex = styled(Box)
    (({ theme }) => `
        gap: .875rem;
        display: flex;
        justify-content: space-between;

        @media(min-width: ${theme.breakpoints.values.md}px) {
            gap: 1.875rem;
        }
`)

const FormVehicle = ({ handleReturnFormData }: ParamsVehicleDatas) => {
    const { dataCar, handleSearchCar } = useContext(DataContext);
    const { register, setValue, watch, formState: { errors } } = useForm<ParamsVehicle>();

    const [years, setYears] = useState<Array<ParamsArray>>([]);
    const [brands, setBrands] = useState<Array<ParamsArray>>([]);
    const [colors, setColors] = useState<Array<ParamsArray>>([]);
    const [models, setModels] = useState<Array<ParamsArray>>([]);
    const [versions, setVersions] = useState<Array<ParamsArray>>([]);
    const [yearsManufacturing, setYearsManufacturing] = useState<Array<ParamsArray>>([]);

    const year = watch('year');
    const brand = watch('brand');
    const color = watch('color');
    const model = watch('model');
    const chassis = watch('chassis');
    const version = watch('version');
    const versionId = watch('versionId');
    const yearManufacture = watch('yearManufacture');

    const handleSearchCarDetails = (type: "brands" | "models" | "versions" | "year" | "colors", id: number) => {
        handleSearchCar({ typeDetailsCar: type, codDetailCar: id });
    }

    useEffect(() => {
        if (dataCar?.brands) {
            const listArray = dataCar?.brands.map(({ NM_MARCA, CD_MARCA }) => { return { text: NM_MARCA, value: `${CD_MARCA}` } });
            setBrands(listArray);
        }

        if (dataCar?.models) {
            const listArray = dataCar?.models.map(({ NM_MODELO_BASICO, CD_MODELO_BASICO }) => { return { text: NM_MODELO_BASICO, value: `${CD_MODELO_BASICO}` } });
            setModels(listArray);
        }

        if (dataCar?.versions) {
            const listArray = dataCar?.versions.map(({ NM_MODELO, CD_MODELO }) => { return { text: NM_MODELO, value: `${CD_MODELO}` } });
            setVersions(listArray);
        }

        if (dataCar?.year) {
            const listArray = dataCar?.year.map(({ NR_ANO, CD_MODELO_ANO }) => { return { text: `${NR_ANO}`, value: `${CD_MODELO_ANO}` } });
            setYears(listArray);
        }

        if (dataCar?.colors) {
            const listArray = dataCar?.colors.map(({ DS_COR }) => { return { text: DS_COR, value: DS_COR } });
            setColors(listArray);
        }

        if (year && dataCar?.year) {
            let listArray: Array<ParamsArray> = [];
            // eslint-disable-next-line array-callback-return
            dataCar.year.map(({ NR_ANO, CD_MODELO_ANO }) => (parseFloat(year?.text) >= NR_ANO) && listArray.push({ text: `${NR_ANO}`, value: `${CD_MODELO_ANO}` }));

            listArray && setYearsManufacturing(listArray);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataCar, year]);

    useEffect(() => {
        if (year && version && yearManufacture && color && chassis?.match(Regex.chassisOrBoard)) {
            handleReturnFormData({ year: year.text, version: parseFloat(version.value), chassis, yearManufacture: yearManufacture.text, color: color.value });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [year, versionId, chassis, yearManufacture]);

    return (
        <ContentFadeAnimation>
            <Box sx={{
                display: 'flex',
                gap: '1.125rem',
                flexDirection: 'column'
            }}>
                <TitleForm>
                    Dados do veículo
                </TitleForm>
                <BoxFlex>
                    <Select
                        label='Marca'
                        placeholder='Selecione'
                        itemSelected={brand}
                        listItems={brands}
                        onChangeSelected={(e => { setValue('brand', e); handleSearchCarDetails('models', parseFloat(e?.value)) })}
                    />

                    <Select
                        label='Modelo'
                        placeholder='Selecione'
                        itemSelected={model}
                        listItems={models}
                        onChangeSelected={(e => { setValue('model', e); handleSearchCarDetails('versions', parseFloat(e?.value)) })} />
                </BoxFlex>

                <Select
                    label='Versão'
                    placeholder='Selecione'
                    itemSelected={version}
                    listItems={versions}
                    onChangeSelected={(e => { setValue('version', e); handleSearchCarDetails('year', parseFloat(e?.value)) })} />

                <BoxFlex>
                    <Select
                        label='Ano Modelo'
                        placeholder='Selecione'
                        itemSelected={year}
                        listItems={years}
                        onChangeSelected={(e) => setValue('year', e)}
                    />
                    <Select
                        label='Cor'
                        placeholder='Selecione'
                        itemSelected={color}
                        listItems={colors}
                        onChangeSelected={(e) => setValue('color', e)}
                    />
                </BoxFlex>

                <BoxFlex>
                    <Select
                        label='Ano Fabricação'
                        placeholder='Selecione'
                        itemSelected={yearManufacture}
                        listItems={yearsManufacturing}
                        onChangeSelected={(e) => setValue('yearManufacture', e)}
                    />
                    <Input
                        name='chassis'
                        label='Chassi do carro'
                        placeholder='*************9999'
                        type='text'
                        mask
                        typeIdentity='plaque'
                        error={errors.chassisOrBoard}
                        register={{ ...register("chassis", { pattern: /^[a-z0-9]{13}([0-9]{4}|[0-9]{3}_)$/i }) }}
                        uppercase
                    />
                </BoxFlex>
            </Box>
        </ContentFadeAnimation>
    );
}

export default FormVehicle;